// FILE LOCATION: src/utils/csvConverter.js
// Convert question banks to D2L-compatible CSV format

function escapeCSV(text) {
  if (typeof text !== 'string') return text;
  let escapedText = text.replace(/"/g, '""');
  if (/[",\n\r]/.test(escapedText)) {
    escapedText = `"${escapedText}"`;
  }
  return escapedText;
}

function cleanAnswer(text) {
  return text.replace(/^\*\s*/, '').trim();
}

export function extractQuestionBankToCSV(contentHtml) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(contentHtml, 'text/html');
  
  const csvRows = [];
  let capturing = false;
  let currentQuestion = null;
  
  // Extract all text content from the document
  const textLines = [];
  const allElements = doc.body.querySelectorAll('*');
  
  allElements.forEach(element => {
    // Handle paragraphs
    if (element.tagName === 'P') {
      const text = element.textContent?.trim();
      if (text) {
        textLines.push(text);
      }
    }
    // Handle lists
    else if (element.tagName === 'UL' || element.tagName === 'OL') {
      const listItems = element.querySelectorAll('li');
      listItems.forEach(li => {
        const text = li.textContent?.trim();
        if (text) {
          textLines.push(text);
        }
      });
    }
  });
  
  // Process each line
  for (let i = 0; i < textLines.length; i++) {
    const line = textLines[i];
    
    // Check for new question
    const questionMatch = line.match(/^Question\s+(MC|MS|TF|SA|M|O):\s*(.+)/i);
    if (questionMatch) {
      // Save previous question if exists
      if (currentQuestion) {
        addQuestionToCSV(csvRows, currentQuestion);
      }
      
      const questionType = questionMatch[1].toUpperCase();
      const questionText = questionMatch[2].trim();
      
      currentQuestion = {
        type: questionType,
        text: questionText,
        answers: [],
        metadata: {}
      };
      capturing = true;
      continue;
    }
    
    // Stop capturing on DESCRIPTION
    if (line.startsWith('DESCRIPTION:')) {
      if (currentQuestion) {
        addQuestionToCSV(csvRows, currentQuestion);
      }
      capturing = false;
      currentQuestion = null;
      continue;
    }
    
    // Process question content
    if (capturing && currentQuestion) {
      // Check for metadata
      if (line.match(/^(Points|Difficulty|Hint|Feedback):/i)) {
        const [key, ...valueParts] = line.split(':');
        const value = valueParts.join(':').trim();
        currentQuestion.metadata[key.toLowerCase()] = value;
        continue;
      }
      
      // Process answers based on question type
      if (currentQuestion.type === 'MC' || currentQuestion.type === 'MS') {
        const answerMatch = line.match(/^(\*?)([a-z]\.?\s*)?(.+)/i);
        if (answerMatch) {
          const isCorrect = answerMatch[1] === '*';
          const answerText = answerMatch[3].trim();
          if (answerText && !answerText.match(/^(Points|Difficulty|Hint|Feedback):/i)) {
            currentQuestion.answers.push({
              text: answerText,
              correct: isCorrect
            });
          }
        }
      } else if (currentQuestion.type === 'TF') {
        const tfMatch = line.match(/^(\*?)(True|False)/i);
        if (tfMatch) {
          const isCorrect = tfMatch[1] === '*';
          const answerValue = tfMatch[2].toLowerCase();
          currentQuestion.answers.push({
            text: answerValue,
            correct: isCorrect
          });
        }
      } else if (currentQuestion.type === 'SA') {
        if (!line.match(/^[a-z]\.?\s/i) && !line.match(/^\*?[a-z]\.?\s/i) && line.trim()) {
          currentQuestion.answers.push({
            text: line.trim(),
            correct: true
          });
        } else {
          const answerMatch = line.match(/^(\*?)([a-z]\.?\s*)?(.+)/i);
          if (answerMatch) {
            const answerText = answerMatch[3].trim();
            currentQuestion.answers.push({
              text: answerText,
              correct: true
            });
          }
        }
      } else if (currentQuestion.type === 'M') {
        const matchPair = line.split('/').map(s => s.trim());
        if (matchPair.length === 2) {
          currentQuestion.answers.push({
            choice: matchPair[0],
            match: matchPair[1]
          });
        } else if (line.includes('/')) {
          const parts = line.split('/');
          if (parts.length === 2 && parts[0].trim() === '') {
            currentQuestion.answers.push({
              choice: '',
              match: parts[1].trim()
            });
          }
        }
      } else if (currentQuestion.type === 'O') {
        let orderText = line;
        orderText = orderText.replace(/^\d+[,.)]\s*/, '');
        orderText = orderText.replace(/\(Step \d+\)\s*$/, '');
        if (orderText.trim()) {
          currentQuestion.answers.push({
            text: orderText.trim(),
            order: currentQuestion.answers.length + 1
          });
        }
      }
    }
  }
  
  // Add the last question
  if (currentQuestion) {
    addQuestionToCSV(csvRows, currentQuestion);
  }
  
  return csvRows.join('\n');
}

function addQuestionToCSV(csvRows, question) {
  // Add question header
  if (question.type === 'MC') {
    csvRows.push('NewQuestion,MC,,');
  } else if (question.type === 'MS') {
    csvRows.push('NewQuestion,MS,,');
  } else if (question.type === 'TF') {
    csvRows.push('NewQuestion,TF,,');
  } else if (question.type === 'SA') {
    csvRows.push('NewQuestion,SA,,');
  } else if (question.type === 'M') {
    csvRows.push('NewQuestion,M,,');
  } else if (question.type === 'O') {
    csvRows.push('NewQuestion,O,,');
  }
  
  // Add question text
  csvRows.push(`QuestionText,${escapeCSV(question.text)},,`);
  
  // Add answers
  if (question.type === 'MC' || question.type === 'MS') {
    question.answers.forEach(answer => {
      const score = answer.correct ? '100' : '0';
      csvRows.push(`Option,${score},${escapeCSV(answer.text)},,`);
    });
  } else if (question.type === 'TF') {
    const trueAnswer = question.answers.find(a => a.text.toLowerCase() === 'true');
    const falseAnswer = question.answers.find(a => a.text.toLowerCase() === 'false');
    
    if (trueAnswer) {
      const trueScore = trueAnswer.correct ? '100' : '0';
      csvRows.push(`TRUE,${trueScore},,`);
    }
    if (falseAnswer) {
      const falseScore = falseAnswer.correct ? '100' : '0';
      csvRows.push(`FALSE,${falseScore},,`);
    }
    
    if (question.answers.length === 1) {
      const answer = question.answers[0];
      if (answer.text.toLowerCase() === 'true') {
        csvRows.push(`TRUE,${answer.correct ? '100' : '0'},,`);
        csvRows.push(`FALSE,${answer.correct ? '0' : '100'},,`);
      } else {
        csvRows.push(`FALSE,${answer.correct ? '100' : '0'},,`);
        csvRows.push(`TRUE,${answer.correct ? '0' : '100'},,`);
      }
    }
  } else if (question.type === 'SA') {
    question.answers.forEach(answer => {
      csvRows.push(`Answer,100,${escapeCSV(answer.text)},,`);
    });
  } else if (question.type === 'M') {
    question.answers.forEach((answer, index) => {
      if (answer.choice && answer.match) {
        csvRows.push(`Choice,${index + 1},${escapeCSV(answer.choice)},,`);
      } else if (!answer.choice && answer.match) {
        csvRows.push(`Choice,${index + 1},${escapeCSV(answer.match)},,`);
      }
    });
    
    question.answers.forEach((answer, index) => {
      if (answer.choice && answer.match) {
        csvRows.push(`Match,${index + 1},${escapeCSV(answer.match)},,`);
      }
    });
  } else if (question.type === 'O') {
    question.answers.forEach((answer) => {
      csvRows.push(`Item,${escapeCSV(answer.text)},NOT HTML,,`);
    });
  }
  
  // Add metadata
  if (question.metadata.points) {
    csvRows.push(`Points,${question.metadata.points},,`);
  }
  if (question.metadata.difficulty) {
    csvRows.push(`Difficulty,${escapeCSV(question.metadata.difficulty)},,`);
  }
  if (question.metadata.hint) {
    csvRows.push(`Hint,${escapeCSV(question.metadata.hint)},,`);
  }
  if (question.metadata.feedback) {
    csvRows.push(`Feedback,${escapeCSV(question.metadata.feedback)},,`);
  }
}

export async function saveCsvToFolder(folderHandle, csvContent, filename) {
  try {
    const coursePagesHandle = await folderHandle.getDirectoryHandle('course-pages', { 
      create: true 
    });

    let csvFilename = String(filename);
    if (csvFilename.includes('.')) {
      csvFilename = csvFilename.substring(0, csvFilename.lastIndexOf('.'));
    }
    csvFilename = csvFilename.replace(/[^\w\s-]/gi, '').replace(/\s+/g, '-').toLowerCase();
    csvFilename = csvFilename + '.csv';

    const fileHandle = await coursePagesHandle.getFileHandle(csvFilename, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(csvContent);
    await writable.close();

    return { success: true, filename: csvFilename };
  } catch (error) {
    console.error('Error saving CSV file:', error);
    return { success: false, error: error.message };
  }
}